import { Dispatch } from 'redux';
import LocalStorageUtils from '../../../utils/local-storage';
import Errors from '../../../shared/validation/errors';
import setLoadingStatus, { setLoadingStatusMastersFetch } from '../setLoadingStatus';
import headersBuilder, { Method } from '../../../utils/request';
import { Master } from '../../../types/masterTypes';
import { PaginationInfo } from '../../../types/sharedTypes';
import { PAMMState } from '../../reducers/rootReducer';
import { AccountType } from '../../../types/authTypes';
import { fetchMasterDetails } from './fetchMasterDetails';
import { fetchInvestorDetails } from '../investor/fetchInvestorDetails';

export const FETCH_MASTERS = 'FETCH_MASTERS';
export const FETCH_MASTERS_DEFAULT = 'FETCH_MASTERS_DEFAULT';
export const UPDATE_MASTERS_CURRENT_PAGE_INFO = 'UPDATE_MASTERS_CURRENT_PAGE_INFO';

export type FetchMastersAction = {
  type: string;
  masters: Array<Master>;
  paginationInfo: PaginationInfo;
};

export const fetchMasters = (masters: Array<Master>, paginationInfo: PaginationInfo): FetchMastersAction => {
  return {
    type: FETCH_MASTERS,
    masters,
    paginationInfo
  };
};

// masters default details on investors pages
export type FetchMastersDefaultAction = {
  type: string;
  mastersDefault: Array<Master>;
  paginationInfo: PaginationInfo;
};

export const fetchMastersDefault = (mastersDefault: Array<Master>, paginationInfo: PaginationInfo): FetchMastersDefaultAction => {
  return {
    type: FETCH_MASTERS_DEFAULT,
    mastersDefault,
    paginationInfo
  };
};

export type UpdateMastersCurrentPage = {
  type: string;
  currentPageInfo: { limit: number; currentPage: number };
};

export const updateMastersCurrentPage = (currentPageInfo: { limit: number; currentPage: number }): UpdateMastersCurrentPage => {
  return {
    type: UPDATE_MASTERS_CURRENT_PAGE_INFO,
    currentPageInfo
  };
};

// TODO переделать получение инфы о пользователе
export const getInfoMastersThunkAC =
  () =>
  async (dispatch: Dispatch, getState: () => PAMMState): Promise<void> => {
    const { api } = getState().configReducer;
    const userLS = LocalStorageUtils.getValueFromLocalStorage('user');
    const userInfoAtLSJson = userLS ? JSON.parse(userLS) : {};

    // TODO исправить бек на инвесторе Value на MM Values
    const generateAccountOptions = () => {
      switch (userInfoAtLSJson.AccountType) {
        case AccountType.Master:
          return 'returnAccountOptionsValues=true';

        case AccountType.Investor:
          return 'returnAccountOptionsValue=true';

        default:
          return '';
      }
    };

    const generateFetchRoute = () => {
      switch (userInfoAtLSJson.AccountType) {
        case AccountType.Master:
          return 'masters';

        case AccountType.Admin:
          return 'admins';

        case AccountType.Investor:
          return 'investors';

        default:
          return '';
      }
    };
    if (userInfoAtLSJson.AccountType !== AccountType.Admin) {
      try {
        await dispatch(setLoadingStatus(true));

        const fetchRoute = generateFetchRoute();
        let userId = userInfoAtLSJson?.Id;

        if (fetchRoute === 'investors') {
          const linkedId = LocalStorageUtils.getValueFromLocalStorage('linkedIdMobile');
          if (linkedId) {
            userId = linkedId;
          }
        }

        const response = await fetch(`${api}/${fetchRoute}/${userId}?${generateAccountOptions()}`, headersBuilder(Method.GET));
        const userData = await response.json();
        const updateUser = {
          ...userInfoAtLSJson,
          AccountInfo: userData
        };
        if (userInfoAtLSJson.AccountType === AccountType.Investor) {
          dispatch(fetchInvestorDetails(userData));
        }
        if (userInfoAtLSJson.AccountType === AccountType.Master) {
          LocalStorageUtils.putValueToLocalStorage('user', JSON.stringify(updateUser));
          dispatch(fetchMasterDetails(userData));
        }
        dispatch(setLoadingStatus(false));
      } catch (e) {
        if (e instanceof Error) {
          Errors.throwErrorMessage(e);
        } else {
          Errors.throwErrorMessage(new Error(String(e)));
        }
      }
    }
  };

function fetchMastersAsync(
  api: string,
  showInactive: boolean,
  showTest: boolean,
  hideNegativeProfitMasters: boolean | null,
  page?: number,
  limit?: number | string,
  query?: string,
  sortField?: string,
  direction?: string,
  returnAccountOptionsValues?: boolean | null,
  isAuth?: boolean
): any {
  let queryString = `?page=${page || 1}`;

  if (hideNegativeProfitMasters) {
    queryString += '&hideNegativeProfitMasters=true';
  } else {
    queryString += '&hideNegativeProfitMasters=false';
  }
  if (limit) queryString += `&limit=${limit}`;
  if (query) queryString += `&query=${query}`;
  if (sortField) queryString += `&sortField=${sortField}`;
  if (direction) queryString += `&direction=${direction}`;
  if (returnAccountOptionsValues) queryString += `&returnAccountOptionsValues=${returnAccountOptionsValues}`;
  if (showInactive && showTest) {
    queryString += '&filter=disabled,test';
  } else if (showInactive) {
    queryString += '&filter=disabled';
  } else if (showTest) {
    queryString += '&filter=test';
  }

  if (!LocalStorageUtils.localStorageKeyExists('masters')) {
    LocalStorageUtils.putValuesToLocalStorage('masters', [
      'id=true',
      'login=true',
      'name=true',
      'currency=true',
      'balance=true',
      'credit=true',
      'equity=true',
      'profit=true',
      'margin=true',
      'ownFunds=true',
      'connect=true'
    ]);
  }

  if (!LocalStorageUtils.localStorageKeyExists('showInactiveMasters')) {
    LocalStorageUtils.putValueToLocalStorage('showInactiveMasters', 'false');
  }

  if (!LocalStorageUtils.localStorageKeyExists('showTestMasters')) {
    LocalStorageUtils.putValueToLocalStorage('showTestMasters', 'false');
  }

  if (!LocalStorageUtils.localStorageKeyExists('hideNegativeProfitMasters')) {
    LocalStorageUtils.putValueToLocalStorage('hideNegativeProfitMasters', 'false');
  }

  return (dispatch: Dispatch) => {
    dispatch(setLoadingStatus(true));
    dispatch(setLoadingStatusMastersFetch(true));
    // TODO fetch
    fetch(
      `${api}/masters${!isAuth ? '' : '/shared'}${queryString}`,
      !isAuth
        ? headersBuilder(Method.GET)
        : {
            method: 'GET',
            body: undefined,
            headers: {
              'Content-Type': 'application/json'
            }
          }
    )
      .then((response: Response) => Errors.checkResponseForErrors(response, api))
      .then((data: any) => {
        if (returnAccountOptionsValues) {
          dispatch(fetchMastersDefault(data.Data, data.PaginationInfo));
          dispatch(
            updateMastersCurrentPage({ limit: data.PaginationInfo.RecordsOnPage, currentPage: data.PaginationInfo.CurrentPage })
          );
        } else {
          dispatch(fetchMasters(data.Data, data.PaginationInfo));
          dispatch(
            updateMastersCurrentPage({ limit: data.PaginationInfo.RecordsOnPage, currentPage: data.PaginationInfo.CurrentPage })
          );
        }
      })
      .catch((error: Error) => Errors.throwErrorMessage(error))
      .finally(() => {
        dispatch(setLoadingStatus(false));
        dispatch(setLoadingStatusMastersFetch(false));
      });
  };
}

export default fetchMastersAsync;
